<template>
  <div class="profile">
    <header>
      <h1>{{ t('profile.title') }}</h1>
      <p class="lead">{{ t('profile.lead') }}</p>
    </header>

    <div class="pane-tabs" role="tablist" :aria-label="t('profile.title')">
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'you' }"
        :aria-selected="pane === 'you'"
        @click="setPane('you')"
      >
        {{ t('profile.tabs.you') }}
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'case' }"
        :aria-selected="pane === 'case'"
        @click="setPane('case')"
      >
        {{ t('profile.tabs.case') }}
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'look' }"
        :aria-selected="pane === 'look'"
        @click="setPane('look')"
      >
        {{ t('profile.tabs.look') }}
      </button>
    </div>

    <div v-show="pane === 'you'">
    <section class="panel">
      <h2>{{ t('profile.account') }}</h2>
      <p class="hint">{{ t('profile.nameHint') }}</p>
      <p v-if="nameLocked" class="hint">
        {{ t('profile.nameCooldown', { date: nameCooldownDate }) }}
      </p>
      <form class="name-form" @submit.prevent="onSaveName">
        <label class="visually-hidden" for="profile-username">
          {{ t('profile.name') }}
        </label>
        <input
          id="profile-username"
          v-model="nameDraft"
          class="text-input"
          type="text"
          autocomplete="nickname"
          :maxlength="usernameMaxLen"
          :placeholder="t('profile.placeholder')"
          :disabled="nameLocked || auth.busy"
        />
        <button
          type="submit"
          class="save-btn"
          :disabled="auth.busy || !nameDirty || nameLocked"
        >
          {{ t('profile.save') }}
        </button>
      </form>
      <p v-if="nameErrorKey" class="name-error">{{ t(nameErrorKey) }}</p>
      <p v-else-if="nameSaved" class="saved">{{ t('profile.saved') }}</p>
      <dl class="account">
        <div>
          <dt>{{ t('auth.email') }}</dt>
          <dd>{{ auth.user?.email || '—' }}</dd>
        </div>
      </dl>
      <div class="account-actions">
        <router-link :to="publicPath" class="ghost-link">
          {{ t('profile.public.view') }}
        </router-link>
        <button type="button" class="save-btn" @click="onLogout">
          {{ t('auth.logout') }}
        </button>
      </div>
    </section>

    <section class="panel level-panel">
      <h2>{{ t('level.title') }}</h2>
      <p class="hint">{{ t('level.lead') }}</p>
      <p class="level-name">{{ t('level.chip', { n: progress.level }) }}</p>
      <div
        class="level-bar"
        role="progressbar"
        :aria-valuenow="progress.xpIntoLevel"
        :aria-valuemin="0"
        :aria-valuemax="progress.xpToNext"
        :aria-label="t('level.title')"
      >
        <span class="level-bar-fill" :style="{ width: progress.barPct + '%' }" />
      </div>
      <p class="level-meta">
        {{
          t('level.bar', {
            into: progress.xpIntoLevel,
            need: progress.xpToNext,
          })
        }}
      </p>
      <p v-if="nextRewardText" class="level-next">{{ nextRewardText }}</p>
      <button
        v-if="banlistReward.available > 0"
        type="button"
        class="save-btn level-reward"
        @click="banlistReward.openDialog()"
      >
        {{ t('banlistReward.profileChoose') }}
      </button>
      <p v-if="banlistReward.available > 0" class="level-next">
        {{ t('banlistReward.profileLeft', { n: banlistReward.available }) }}
      </p>
      <p v-else-if="banlistNextLevel != null" class="level-next">
        {{ t('banlistReward.profileNext', { n: banlistNextLevel }) }}
      </p>
      <router-link to="/rank?board=level" class="ghost-link level-ladder">
        {{ t('level.viewLadder') }}
      </router-link>
    </section>

    <section class="panel rank-panel">
      <h2>{{ t('rank.title') }}</h2>
      <p class="hint">{{ t('rank.lead') }}</p>
      <div v-if="standing" class="rank-self">
        <RankBadge
          :league="standing.league"
          :division="standing.division"
          :placement-remaining="standing.placementRemaining"
          :size="88"
        />
        <div>
          <p class="rank-name">{{ standingLabel }}</p>
          <p v-if="standingLp" class="rank-lp">{{ standingLp }}</p>
          <router-link to="/rank" class="ghost-link">
            {{ t('rank.viewLadder') }}
          </router-link>
        </div>
      </div>
      <p v-else class="hint">{{ t('rank.loading') }}</p>
    </section>

    <section class="panel avatar-panel">
      <h2>{{ t('profile.avatar.title') }}</h2>

      <div class="avatar-layout">
        <div class="preview-col">
          <AvatarView :config="player.avatar" :size="previewSize" />
          <div class="photo-row">
            <AvatarView
              :config="player.avatar"
              crop="bust"
              circle
              :size="88"
              :label="t('profile.avatar.photo')"
            />
            <span class="photo-cap">{{ t('profile.avatar.photo') }}</span>
          </div>
          <div class="preview-actions">
            <button type="button" class="ghost-btn" @click="shuffle">
              {{ t('profile.avatar.random') }}
            </button>
            <button type="button" class="ghost-btn" @click="resetLook">
              {{ t('profile.avatar.reset') }}
            </button>
          </div>
        </div>

        <div class="picker-col">
          <h3>{{ t('profile.avatar.artTitle') }}</h3>
          <div class="part-grid">
            <button
              type="button"
              class="part-card"
              :class="{ on: player.avatar.cardId === LAST_CALL_PORTRAIT_ID }"
              :disabled="!lastCall.portraitUnlocked"
              @click="pickCard(LAST_CALL_PORTRAIT_ID)"
            >
              <AvatarView
                :config="{ cardId: LAST_CALL_PORTRAIT_ID, frameId: player.avatar.frameId }"
                :size="104"
              />
              <span>{{ t('missions.lastCall.portrait') }}</span>
            </button>
            <button
              v-for="cardId in cardIds"
              :key="cardId"
              type="button"
              class="part-card"
              :class="{ on: player.avatar.cardId === cardId }"
              @click="pickCard(cardId)"
            >
              <AvatarView
                :config="{ cardId, frameId: player.avatar.frameId }"
                :size="104"
              />
              <span>{{ localizedName(cardId) }}</span>
            </button>
          </div>

          <h3 class="frames-title">{{ t('profile.avatar.framesTitle') }}</h3>
          <div class="frame-grid">
            <button
              v-for="frameId in frameIds"
              :key="frameId"
              type="button"
              class="part-card"
              :class="{ on: player.avatar.frameId === frameId }"
              :disabled="frameLocked(frameId)"
              @click="pickFrame(frameId)"
            >
              <AvatarView
                :config="{ cardId: player.avatar.cardId, frameId }"
                :size="80"
              />
              <span>{{ t(`profile.avatar.frames.${frameId}`) }}</span>
            </button>
          </div>

          <h3 class="frames-title">{{ t('settings.leagueRewards') }}</h3>
          <p class="hint">{{ t('profile.avatar.leagueHint') }}</p>
          <div class="frame-grid">
            <button
              v-for="set in leagueSets"
              :key="set.frameId"
              type="button"
              class="part-card league"
              :class="{ on: player.avatar.frameId === set.frameId }"
              :disabled="frameLocked(set.frameId)"
              :title="leagueTitle(t(`profile.avatar.frames.${set.frameId}`), set.league)"
              @click="pickFrame(set.frameId)"
            >
              <AvatarView
                :config="{ cardId: player.avatar.cardId, frameId: set.frameId }"
                :size="80"
              />
              <span>{{ t(`profile.avatar.frames.${set.frameId}`) }}</span>
              <img
                v-if="frameLocked(set.frameId)"
                class="league-lock"
                :src="leagueBadgeSrc(set.league) ?? ''"
                alt=""
              />
            </button>
          </div>
        </div>
      </div>
    </section>
    </div>

    <section v-show="pane === 'case'" class="panel case-panel">
      <h2>{{ t('profile.showcase.title') }}</h2>
      <p class="hint">{{ t('profile.showcase.lead') }}</p>
      <FoilShowcaseEditor />
    </section>

    <section v-show="pane === 'look'" class="panel look-panel">
      <h2>{{ t('profile.look.title') }}</h2>
      <p class="hint">{{ t('profile.look.lead') }}</p>
      <h3>{{ t('settings.sleeve') }}</h3>
      <p class="hint">{{ t('settings.sleeveHint') }}</p>
      <SleevePicker tone="menu" />
      <h3>{{ t('settings.mat') }}</h3>
      <p class="hint">{{ t('settings.matHint') }}</p>
      <PlaymatPicker tone="menu" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  AVATAR_CARD_IDS,
  AVATAR_FRAME_IDS,
  DEFAULT_AVATAR,
  LAST_CALL_FRAME_ID,
  LAST_CALL_PORTRAIT_ID,
  LEAGUE_COSMETICS,
  LEAGUE_FRAME_IDS,
  LEAGUE_IDS,
  leagueBadgeSrc,
  randomAvatar,
  type LeagueId,
  nextBanlistRewardLevel,
  type RankedStanding,
} from '@shared/index'
import { useI18n } from '../i18n/t'
import { localizedName } from '../i18n/localizeCard'
import { USERNAME_MAX_LEN, usePlayerStore } from '../stores/player'
import { useAuthStore } from '../stores/auth'
import { intlLocale, useLocaleStore } from '../stores/locale'
import { ApiError } from '../api/http'
import { useProgressStore } from '../stores/progress'
import { useBanlistRewardStore } from '../stores/banlistReward'
import { useLastCallStore } from '../stores/lastCall'
import { useLeagueRewardsStore } from '../stores/leagueRewards'
import AvatarView from '../components/AvatarView.vue'
import RankBadge from '../components/RankBadge.vue'
import SleevePicker from '../components/SleevePicker.vue'
import PlaymatPicker from '../components/PlaymatPicker.vue'
import FoilShowcaseEditor from '../components/FoilShowcaseEditor.vue'
import { fetchMyRating } from '../api/rating'
import { publicProfilePath } from '../api/profile'
import { useCompactLayout } from '../composables/useCompactLayout'

type ProfilePane = 'you' | 'case' | 'look'

const { t } = useI18n()
const { menusShort } = useCompactLayout()
const route = useRoute()
const router = useRouter()
const player = usePlayerStore()
const auth = useAuthStore()
const localeStore = useLocaleStore()
const progress = useProgressStore()
const banlistReward = useBanlistRewardStore()
const lastCall = useLastCallStore()
const leagueRewards = useLeagueRewardsStore()
const cardIds = AVATAR_CARD_IDS
const frameIds = AVATAR_FRAME_IDS.filter((id) => !LEAGUE_FRAME_IDS.includes(id))
const leagueSets = LEAGUE_IDS.map((league) => ({ league, ...LEAGUE_COSMETICS[league] }))
let persistTimer: ReturnType<typeof setTimeout> | null = null
const standing = ref<RankedStanding | null>(null)
const nameDraft = ref(player.username)
const nameSaved = ref(false)
const nameErrorKey = ref('')
const usernameMaxLen = USERNAME_MAX_LEN
let nameSavedTimer: ReturnType<typeof setTimeout> | null = null

const nameDirty = computed(
  () => nameDraft.value.trim() !== player.username,
)
const nameLocked = computed(() => {
  const raw = auth.usernameRenameAt
  if (!raw) return false
  const at = new Date(raw).getTime()
  return Number.isFinite(at) && at > Date.now()
})
const nameCooldownDate = computed(() => {
  const raw = auth.usernameRenameAt
  if (!raw) return ''
  const at = new Date(raw)
  if (Number.isNaN(at.getTime())) return ''
  return new Intl.DateTimeFormat(intlLocale(localeStore.locale), {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(at)
})

watch(
  () => player.username,
  (name) => {
    nameDraft.value = name
  },
)

const previewSize = computed(() => (menusShort.value ? 140 : 232))
const publicPath = computed(() => publicProfilePath(player.username))

function paneFromHash(hash: string): ProfilePane {
  if (hash === '#showcase' || hash === '#case') return 'case'
  if (hash === '#look') return 'look'
  return 'you'
}

const pane = ref<ProfilePane>(paneFromHash(route.hash))

function setPane(next: ProfilePane): void {
  pane.value = next
  const hash =
    next === 'case' ? '#showcase' : next === 'look' ? '#look' : ''
  if (route.hash === hash) return
  void router.replace({ hash })
}

watch(
  () => route.hash,
  (hash) => {
    pane.value = paneFromHash(hash)
  },
)

const standingLabel = computed(() => {
  const s = standing.value
  if (!s) return ''
  if (s.placementRemaining > 0 || !s.league) {
    return t('rank.placementLeft', { n: s.placementRemaining })
  }
  const name = t(`rank.leagues.${s.league}`)
  if (!s.division || s.league === 'king') return name
  return `${name} ${s.division === 1 ? 'I' : 'II'}`
})

const standingLp = computed(() => {
  const s = standing.value
  if (!s || s.placementRemaining > 0 || !s.league) return ''
  if (s.league === 'king') return t('rank.lpBare', { lp: s.lp })
  return t('rank.lpCount', { lp: s.lp })
})

const banlistNextLevel = computed(() => {
  const anchor = banlistReward.anchorLevel
  if (anchor == null || banlistReward.available > 0) return null
  return nextBanlistRewardLevel(anchor, banlistReward.unlocked)
})

const nextRewardText = computed(() => {
  const reward = progress.nextReward
  if (!reward) return ''
  if (reward.packs > 0 && reward.coins > 0) {
    return t('level.nextBoth', { n: reward.coins })
  }
  if (reward.packs > 0) return t('level.nextPack')
  if (reward.coins > 0) return t('level.nextCoins', { n: reward.coins })
  return ''
})

onMounted(() => {
  void auth.fetchMe()
  const token = auth.token
  if (!token) return
  void fetchMyRating(token)
    .then((payload) => {
      standing.value = payload.standing
    })
    .catch(() => undefined)
})

/**
 * Pushes the current portrait to the account after a short pause.
 */
function queuePersistLook(): void {
  if (persistTimer) clearTimeout(persistTimer)
  persistTimer = setTimeout(() => {
    persistTimer = null
    void auth.patchAvatar(player.avatar).catch(() => undefined)
  }, 400)
}

function onLogout(): void {
  auth.logout()
  void router.push({ name: 'login' })
}

/**
 * Saves the public username. Empty / @ names are rejected by the server.
 */
async function onSaveName(): Promise<void> {
  const next = nameDraft.value.replace(/\s+/g, ' ').trim()
  nameErrorKey.value = ''
  nameSaved.value = false
  if (!next || next.includes('@') || next.length > usernameMaxLen) {
    nameErrorKey.value = 'auth.errors.validation'
    return
  }
  if (next === player.username) return
  try {
    await auth.patchUsername(next)
    nameDraft.value = player.username
    nameSaved.value = true
    if (nameSavedTimer) clearTimeout(nameSavedTimer)
    nameSavedTimer = setTimeout(() => {
      nameSaved.value = false
      nameSavedTimer = null
    }, 2400)
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.code === 'username_taken') {
        nameErrorKey.value = 'auth.errors.username_taken'
        return
      }
      if (err.code === 'validation') {
        nameErrorKey.value = 'auth.errors.validation'
        return
      }
      if (err.code === 'username_cooldown') {
        const retryAt = (err.payload as { retryAt?: unknown } | null)?.retryAt
        if (typeof retryAt === 'string' && retryAt) {
          auth.noteUsernameRenameAt(retryAt)
          return
        }
        nameErrorKey.value = 'auth.errors.username_cooldown'
        return
      }
      if (err.status === 429 || err.code === 'rate_limited') {
        nameErrorKey.value = 'auth.errors.rate_limited'
        return
      }
    }
    nameErrorKey.value = 'auth.errors.network'
  }
}

function shuffle(): void {
  player.setAvatar(randomAvatar())
  queuePersistLook()
}

function resetLook(): void {
  player.setAvatar(DEFAULT_AVATAR)
  queuePersistLook()
}

function pickCard(cardId: string): void {
  if (cardId === LAST_CALL_PORTRAIT_ID && !lastCall.portraitUnlocked) return
  player.setAvatar({ ...player.avatar, cardId })
  queuePersistLook()
}

function frameLocked(frameId: string): boolean {
  if (frameId === LAST_CALL_FRAME_ID) return !lastCall.frameUnlocked
  return !leagueRewards.frameUnlocked(frameId)
}

function leagueTitle(name: string, league: LeagueId): string {
  if (leagueRewards.reached(league)) return name
  return `${name} · ${t('settings.leagueLocked', { league: t(`rank.leagues.${league}`) })}`
}

function pickFrame(frameId: string): void {
  if (frameLocked(frameId)) return
  player.setAvatar({ ...player.avatar, frameId })
  queuePersistLook()
}

onUnmounted(() => {
  if (persistTimer) clearTimeout(persistTimer)
  if (nameSavedTimer) clearTimeout(nameSavedTimer)
})
</script>

<style scoped>
.profile {
  width: min(960px, 100%);
  margin: 0 auto;
  padding: 12px 16px 48px;
  font-family: Roboto, system-ui, sans-serif;
  color: var(--md-sys-color-on-surface);
}

h1 {
  margin: 0 0 8px;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
}

.lead {
  margin: 0 0 16px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.pane-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0 0 16px;
}

.pane-tab {
  min-height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.pane-tab.active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.panel {
  padding: 20px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.avatar-panel,
.case-panel,
.look-panel {
  margin-top: 20px;
}

.look-panel h3 {
  margin-top: 20px;
}

.rank-panel {
  margin-top: 20px;
}

.level-panel {
  margin-top: 20px;
}

.level-name {
  margin: 12px 0 8px;
  font-size: 20px;
  font-weight: 600;
}

.level-bar {
  height: 10px;
  border-radius: 999px;
  background: var(--md-sys-color-surface-container-high, #2a2a36);
  overflow: hidden;
}

.level-bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--md-sys-color-primary, #e8c547);
}

.level-meta,
.level-next {
  margin: 8px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.level-next {
  font-weight: 600;
}

.level-reward {
  margin-top: 12px;
}

.rank-self {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0 0 20px;
}

.rank-name {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.rank-lp {
  margin: 4px 0 8px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.account-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.ghost-link {
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 600;
}

.rank-self .ghost-link {
  display: inline-block;
  margin-top: 4px;
}

.level-ladder {
  display: inline-block;
  margin-top: 8px;
}

h2 {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 400;
}

h3 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--md-sys-color-on-surface-variant);
}

.frames-title {
  margin-top: 20px;
}

.hint {
  margin: 0 0 16px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.account {
  display: grid;
  gap: 12px;
  margin: 0 0 16px;
}

.account div {
  display: grid;
  gap: 2px;
}

.account dt {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.account dd {
  margin: 0;
  font-size: 16px;
  word-break: normal;
  overflow-wrap: anywhere;
}

.name-form {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.text-input {
  flex: 1 1 200px;
  min-width: 0;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
}

.text-input:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.save-btn,
.ghost-btn,
.part-card {
  font: inherit;
  cursor: pointer;
}

.save-btn {
  min-height: 40px;
  padding: 0 20px;
  border: 0;
  border-radius: 8px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-size: 14px;
  font-weight: 700;
}

.save-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.save-btn:hover:not(:disabled),
.ghost-btn:hover,
.part-card:hover {
  filter: brightness(1.08);
}

.saved {
  margin: 12px 0 0;
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 500;
}

.name-error {
  margin: 12px 0 0;
  color: #f87171;
  font-size: 14px;
  font-weight: 500;
}

.avatar-layout {
  display: grid;
  grid-template-columns: 272px 1fr;
  gap: 24px;
  align-items: start;
}

@media (max-width: 720px) {
  .avatar-layout {
    grid-template-columns: 1fr;
  }

  h1 {
    font-size: 28px;
    line-height: 36px;
  }

  .profile {
    padding: 20px 16px 40px;
  }
}

.preview-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container-high);
}

.photo-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.photo-cap {
  font-size: 13px;
  color: var(--md-sys-color-on-surface-variant);
}

.preview-actions {
  display: flex;
  gap: 8px;
}

.ghost-btn {
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 8px;
  background: transparent;
  color: var(--md-sys-color-on-surface);
  font-size: 13px;
}

.part-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(128px, 1fr));
  gap: 10px;
}

.frame-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  gap: 10px;
}

.part-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 6px 10px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 10px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font-size: 11px;
  line-height: 14px;
  text-align: center;
}

.part-card.on {
  border-color: var(--md-sys-color-primary);
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.part-card.league {
  position: relative;
}

.part-card.league:disabled {
  cursor: default;
}

.part-card.league:disabled canvas {
  opacity: 0.4;
}

.part-card .league-lock {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 36px;
  height: 36px;
  object-fit: contain;
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
